import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Brain, Send, Edit, AlertCircle,
  CheckCircle, XCircle, Bot, ArrowLeft, FileText, Lightbulb,
  Award, Target, TrendingUp
} from 'lucide-react';
import { usePromptChallenge } from './hooks/usePromptChallenge';
import useCharacters from './hooks/useCharacters';
import Sidebar from './Sidebar';
import './PromptChallengeDetail.css';

const SCORE_COLORS = { 90: '#10B981', 80: '#059669', 70: '#F59E0B', 60: '#F97316', 0: '#EF4444' };
const SCORE_LABELS = { 95: 'Exceptional', 90: 'Excellent', 80: 'Very Good', 70: 'Good', 60: 'Fair', 40: 'Needs Work', 0: 'Poor' };
const EMOTION_MAP  = { 95: 'smiling', 90: 'smiling', 80: 'neutral', 70: 'littleSleepy', 60: 'littleSad', 40: 'sad', 20: 'disappointed', 0: 'crying' };

const getColorByThreshold = (value, map) => {
  const t = Object.keys(map).sort((a, b) => b - a).find(k => value >= k);
  return map[t] || map[0];
};
const getScoreColor      = s => getColorByThreshold(s, SCORE_COLORS);
const getScoreLabel      = s => getColorByThreshold(s, SCORE_LABELS);
const getCharacterEmotion = s => s == null ? 'thinking' : getColorByThreshold(s, EMOTION_MAP);

const CharacterImage = ({ emotion, size = 80, style = {} }) => {
  const { getCharacterImage } = useCharacters();
  return <img src={getCharacterImage(emotion)} alt={`Character ${emotion}`}
    style={{ width: `${size}px`, height: `${size}px`, objectFit: 'contain', ...style }} />;
};

const ScoreCircle = ({ score }) => {
  const scoreColor = score != null ? getScoreColor(score) : '#E5E7EB';
  const textColor  = score != null ? getScoreColor(score) : '#9CA3AF';
  return (
    <div className="score-display">
      <div className="character-feedback">
        <CharacterImage emotion={getCharacterEmotion(score)} style={{ marginBottom: '10px' }} />
      </div>
      <div className="score-circle-prompt" style={{ borderColor: scoreColor, boxShadow: score != null ? `0 0 20px ${scoreColor}33` : 'none' }}>
        <span className="score-number-prompt" style={{ color: textColor }}>{score ?? '--'}</span>
        <span className="score-total-prompt">/100</span>
      </div>
      <div className="score-info-prompt">
        <div className="score-label-prompt" style={{ color: textColor }}>{score != null ? getScoreLabel(score) : 'Pending'}</div>
        <div className="score-icon-prompt">
          {score != null && (score >= 80 ? <CheckCircle style={{ color: scoreColor }} size={20} /> : <XCircle style={{ color: scoreColor }} size={20} />)}
        </div>
      </div>
    </div>
  );
};

const ScoreBreakdown = ({ breakdown }) => {
  if (!breakdown) return null;
  return (
    <div className="score-breakdown-section">
      <div className="breakdown-header"><TrendingUp size={16} style={{ color: '#6366F1' }} /><h6>Detailed Score Breakdown</h6></div>
      <div className="breakdown-grid">
        <div className="breakdown-item">
          <span className="breakdown-label">Constraint Compliance</span>
          <div className="breakdown-bar"><div className="breakdown-fill" style={{ width: `${breakdown.constraintScore}%`, backgroundColor: getScoreColor(breakdown.constraintScore) }} /></div>
          <span className="breakdown-value" style={{ color: getScoreColor(breakdown.constraintScore) }}>{breakdown.constraintScore}/100</span>
        </div>
        <div className="breakdown-item">
          <span className="breakdown-label">Prompt Effectiveness</span>
          <div className="breakdown-bar"><div className="breakdown-fill" style={{ width: `${breakdown.effectivenessScore}%`, backgroundColor: getScoreColor(breakdown.effectivenessScore) }} /></div>
          <span className="breakdown-value" style={{ color: getScoreColor(breakdown.effectivenessScore) }}>{breakdown.effectivenessScore}/100</span>
        </div>
        <div className="breakdown-subscore"><span className="subscore-label">├─ Clarity</span><span className="subscore-value">{breakdown.clarity}/100</span></div>
        <div className="breakdown-subscore"><span className="subscore-label">├─ Specificity</span><span className="subscore-value">{breakdown.specificity}/100</span></div>
        <div className="breakdown-subscore"><span className="subscore-label">└─ Completeness</span><span className="subscore-value">{breakdown.completeness}/100</span></div>
      </div>
    </div>
  );
};

const IdealPromptDisplay = ({ idealPrompt, constraints }) => {
  if (!idealPrompt) return (
    <div className="empty-state">
      <CharacterImage emotion="thinking" size={60} style={{ marginBottom: '10px', opacity: 0.7 }} />
      <p>Ideal prompt example will appear here after submission</p>
    </div>
  );
  return (
    <div className="ideal-prompt-section">
      <div className="ideal-prompt-header"><Lightbulb size={18} style={{ color: '#10B981' }} /><p className="ideal-prompt-title">This should be your prompt for the above challenge to get a high score:</p></div>
      <div className="ideal-prompt-box"><p>{idealPrompt}</p></div>
      {constraints?.length > 0 && (
        <div className="constraints-followed">
          <h6 className="constraints-followed-title">✅ Rules followed by AI:</h6>
          <div className="constraints-checklist">
            {constraints.map((c, i) => (
              <div key={i} className="constraint-check-item"><CheckCircle size={16} style={{ color: '#10B981', flexShrink: 0 }} /><span>{c}</span></div>
            ))}
          </div>
        </div>
      )}
      <small className="ideal-prompt-note">💡 AI-generated example — use as inspiration!</small>
    </div>
  );
};

const IssuesDisplay = ({ issues }) => {
  if (!issues?.length) return (
    <div className="no-issues"><CheckCircle size={16} color="#10B981" /><span>No constraint violations detected!</span></div>
  );
  return (
    <div className="issues-section">
      <h5><AlertCircle size={16} color="#EF4444" /> Issues Found</h5>
      <ul>{issues.map((iss, i) => <li key={i} className="issue-item">{iss}</li>)}</ul>
    </div>
  );
};

/* ═══════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════ */
const PromptChallengeDetail = () => {
  const { language, title } = useParams();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const {
    originalTask, promptChallenge, constraintKey, loading,
    userPrompt, idealPrompt, score, issues, breakdown, submissionHistory,
    isSubmitting, hasAccess, accessError,
    setUserPrompt, handlePromptSubmission,
  } = usePromptChallenge(language, title, navigate);

  if (!hasAccess && accessError) navigate('/challenges', { state: { error: accessError } });

  if (accessError) return (
    <div className="prompt-challenge-container">
      <div className="error-state">
        <CharacterImage emotion="sad" style={{ marginBottom: '20px' }} />
        <p>{accessError}</p>
        <button onClick={() => navigate('/challenges')} className="back-button"><ArrowLeft size={18} /> Back to Challenges</button>
      </div>
    </div>
  );

  if (loading || !originalTask || !promptChallenge) return (
    <div className="prompt-challenge-container">
      <div className="loading-state">
        <CharacterImage emotion="thinking" size={60} style={{ marginBottom: '10px' }} />
        <p>Loading challenge...</p>
        <div className="loading-dots"><span /><span /><span /></div>
      </div>
    </div>
  );

  const current = { score, idealPrompt, issues: issues || [], message: submissionHistory[0]?.message || '', constraints: promptChallenge?.constraints || [] };
  const bestScore = submissionHistory.length > 0 ? Math.max(...submissionHistory.map(s => s.score)) : null;
  const passRate  = submissionHistory.length > 0 ? Math.round((submissionHistory.filter(s => s.success).length / submissionHistory.length) * 100) : 0;
  const words     = userPrompt.trim().split(/\s+/).filter(w => w.length > 0);

  return (
    <div className="pcd-shell">

      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      {sidebarOpen && <div className="pcd-overlay" onClick={() => setSidebarOpen(false)} />}

      <div className={`pcd-main ${sidebarOpen ? 'sidebar-is-open' : ''}`}>

        <button className="pcd-hamburger" onClick={() => setSidebarOpen(v => !v)} title="Toggle menu">
          <span /><span /><span />
        </button>

        <div className="pcd-two-panels">

          {/* ── Panel 1: Challenge Details ── */}
          <div className="pcd-panel pcd-panel-details">
            <div className="column-header">
              <FileText className="column-icon" size={18} />
              <h3 className="left-title">Challenge Details</h3>
            </div>

            <div className="challenge-content">
              <h2 className="main-title">
                <Bot className="title-icon" size={22} /> {originalTask.title}
                <span className={`difficulty ${originalTask.difficulty?.toLowerCase()}`}>{originalTask.difficulty || 'Medium'}</span>
              </h2>

              <div className="objective-section">
                <h5>🎯 Objective:</h5>
                <p>{promptChallenge.objective}</p>
              </div>

              <div className="description-section">
                <h5>📝 Task Description:</h5>
                <p>{originalTask.description}</p>
              </div>

              {/* ✅ FIXED: Test cases are now the only rules — no separate rules section */}
              {originalTask.testCases?.length > 0 && (
                <div className="requirements-section">
                  <h5>⚡ Rules to Follow (follow all to score high):</h5>
                  <div className="constraints-list">
                    {originalTask.testCases.map((tc, i) => (
                      <div key={i} className="constraint-item">
                        <span className="constraint-number">{i + 1}.</span>
                        <span className="constraint-text">{tc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
    {originalTask.remarks && (
                <div className="remarks-section">
                  <h5>💡 Additional Notes:</h5>
                  <p>{originalTask.remarks}</p>
                </div>
              )}
              <div className="scoring-info-section">
                <h5>📊 Scoring System:</h5>
                <div className="scoring-breakdown">
                  <div className="scoring-item"><Target size={14} /><span><strong>60%</strong> - Rule Compliance</span></div>
                  <div className="scoring-item"><Brain size={14} /><span><strong>40%</strong> - Prompt Effectiveness</span></div>
                </div>
                <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '8px' }}>
                  Minimum score to pass: <strong>{promptChallenge.minimumScore || 75}/100</strong>
                </p>
              </div>

          
            </div>
          </div>

          {/* ── Panel 2: Workspace + Results ── */}
          <div className="pcd-panel pcd-panel-workspace-results">

            {/* Workspace */}
            <div className="workspace-section">
              <div className="section-header"><Edit className="section-icon" size={16} /><h3>Prompt Workspace</h3></div>
              <div className="workspace-content">
                <textarea
                  className="prompt-textarea"
                  placeholder="Write your prompt here... Make sure it satisfies all the rules listed on the left."
                  value={userPrompt}
                  onChange={e => setUserPrompt(e.target.value)}
                  rows={7}
                />
                <div className="input-stats">
                  <span>Words: <strong>{words.length}</strong></span>
                  <span>Characters: <strong>{userPrompt.length}</strong></span>
                  <span>Tokens: <strong>~{Math.ceil(words.length * 1.3)}</strong></span>
                </div>
                <button className="submit-prompt-btn" onClick={handlePromptSubmission} disabled={!userPrompt.trim() || isSubmitting}>
                  {isSubmitting ? 'Analyzing & Testing...' : <><Send size={15} /> Submit & Test Prompt</>}
                </button>
              </div>
            </div>

            {/* Results */}
            <div className="unified-results-section">
              <div className="section-header"><Brain className="section-icon" size={16} /><h3>Results & AI Feedback</h3></div>
              <div className="results-flow">
                <div className="result-section">
                  <div className="result-section-title"><Award size={15} /><h4>Score & Performance</h4></div>
                  <div className="score-and-stats">
                    <ScoreCircle score={current.score} />
                    <div className="result-content">
                      <IssuesDisplay issues={current.issues} />
                      {current.message && <div className="message-box"><p>{current.message}</p></div>}
                      <ScoreBreakdown breakdown={breakdown} />
                      <div className="stats-horizontal">
                        <div className="stat-card-score">
                          <span className="stat-label-score">Best Score</span>
                          <span className="stat-value-score" style={{ color: bestScore != null ? getScoreColor(bestScore) : '#9ca3af' }}>{bestScore ?? '--'}</span>
                        </div>
                        <div className="stat-card-score">
                          <span className="stat-label-score">Total Attempts</span>
                          <span className="stat-value-score">{submissionHistory.length || '0'}</span>
                        </div>
                        <div className="stat-card-score">
                          <span className="stat-label-score">Pass Rate</span>
                          <span className="stat-value-score">{submissionHistory.length > 0 ? `${passRate}%` : '0%'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="result-section">
                  <div className="result-section-title"><Award size={15} /><h4>Example Ideal Prompt</h4></div>
                  <div className="result-content">
                    <IdealPromptDisplay idealPrompt={current.idealPrompt} constraints={current.constraints} />
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptChallengeDetail;